/**
 * useLocationsListener Hook
 *
 * Sets up a real-time Firestore listener on the locations config document.
 * When locations are added/modified/deleted in the admin panel, the UI updates immediately.
 *
 * Uses the generic useFirestoreDocument hook for consistency.
 *
 * Features:
 * - Real-time onSnapshot listener (not one-time getDoc)
 * - Updates Zustand locationsStore when config changes
 * - Auto-reconnects on network issues (Firebase SDK built-in)
 * - Cleanup on unmount
 *
 * Usage:
 *   // In AuthenticatedLayout after initial authentication
 *   useLocationsListener();
 */

import { useCallback } from 'react';
import { useFirebaseStore } from '../store/firebaseStore';
import { useLocationsStore } from '../store/locationsStore';
import { useFirestoreDocument } from './firestore';
import { logger } from '../utils/logger';
import type { DocumentData } from 'firebase/firestore';

/**
 * Raw location data structure from Firestore
 */
interface RawLocation {
  id: string;
  name: string;
  address?: string;
}

/**
 * Locations config document structure
 */
interface LocationsConfig {
  locations: RawLocation[];
  defaultLocationId: string;
}

/**
 * Parse Firestore document into LocationsConfig type
 */
function parseLocationsDocument(data: DocumentData): LocationsConfig {
  return {
    locations: Array.isArray(data.locations) ? data.locations : [],
    defaultLocationId: data.defaultLocationId || '11',
  };
}

/**
 * Setup real-time listener for locations config changes
 *
 * This hook should be called once in AuthenticatedLayout.
 * It listens to Firestore `config/locations` document and updates
 * the locationsStore whenever changes are detected.
 *
 * @param enabled - Only activate listener when true (default: true). Pass isConnected
 *                  to prevent false "not found" warnings before Firebase auth completes.
 */
export const useLocationsListener = (enabled: boolean = true): void => {
  const db = useFirebaseStore((state) => state.db);
  const setLocations = useLocationsStore((state) => state.setLocations);
  const setLoading = useLocationsStore((state) => state.setLoading);
  const setError = useLocationsStore((state) => state.setError);
  const setInitialized = useLocationsStore((state) => state.setInitialized);

  // Firestore document path
  const documentPath = 'config/locations';

  // Handle loaded data
  const handleDataLoaded = useCallback(
    (data: LocationsConfig | null) => {
      if (data && data.locations.length > 0) {
        setLocations(data.locations, data.defaultLocationId);
        setInitialized(true);
        logger.log(`[useLocationsListener] Loaded ${data.locations.length} locations from Firestore`);
      } else {
        // Document doesn't exist or is empty - keep using bundled fallback
        logger.warn('[useLocationsListener] No locations in Firestore, using bundled fallback');
        setInitialized(true);
      }
    },
    [setLocations, setInitialized]
  );

  // Handle loading state changes
  const handleLoadingChange = useCallback(
    (loading: boolean) => {
      setLoading(loading);
    },
    [setLoading]
  );

  // Handle errors
  const handleError = useCallback(
    (errorMessage: string | null) => {
      if (errorMessage) {
        logger.error('[useLocationsListener] Error listening to locations config:', errorMessage);
        setError(errorMessage);
        // Still mark as initialized so app doesn't block
        setInitialized(true);
      } else {
        setError(null);
      }
    },
    [setError, setInitialized]
  );

  // Use generic document listener hook
  // Only enable when `enabled` prop is true (prevents false warnings before auth)
  useFirestoreDocument<LocationsConfig>({
    db,
    documentPath,
    parseDocument: parseLocationsDocument,
    onDataLoaded: handleDataLoaded,
    onLoadingChange: handleLoadingChange,
    onError: handleError,
    enabled,
  });
};
